import { Tooltip } from 'flowbite-react';
import React from 'react';
import VerifiedTooltip from './VerifiedTooltip';
import { UIUser, UserExperienceLevelType } from 'types';
import MuiTooltip from '@mui/material/Tooltip';
import { isRecruiterSide } from 'src/config/env.config';
import MuiVerifiedTooltip from './MuiVerifiedTooltip';
import { HiAcademicCap, HiBriefcase, HiLocationMarker } from 'react-icons/hi';
import { useThemeContext } from 'src/context/ThemeProvider';
import { BsGithub, BsLinkedin } from 'react-icons/bs';
import { LuGlobe2 } from 'react-icons/lu';
import classNames from 'classnames';

interface Props {
	userProfileInfo: UIUser;
	isSelf?: boolean;
	hasShownInterest?: boolean;
}

const ProfileExpEduLoc = ({ userProfileInfo, isSelf, hasShownInterest }: Props) => {
	const { themeMode } = useThemeContext();
	const currYear = new Date().getFullYear();

	const specialisation =
		(userProfileInfo?.pifMeta?.underGraduate?.degree || userProfileInfo?.pifMeta?.postGraduate?.degree) &&
		(userProfileInfo?.pifMeta?.underGraduate?.specialisation || userProfileInfo?.pifMeta?.postGraduate?.specialisation)
			? `${userProfileInfo?.pifMeta?.underGraduate?.degree || userProfileInfo?.pifMeta?.postGraduate?.degree} in ${
					userProfileInfo?.pifMeta?.underGraduate?.specialisation ||
					userProfileInfo?.pifMeta?.postGraduate?.specialisation
			  }`
			: '';

	return (
		<>
			{hasShownInterest ? (
				<div className="text-center bg-[#E57A00] text-white text-[8px] @3xl/profile-introduction:text-xs py-[3px] rounded-bl-[5px] rounded-tr-[5px] px-[10px] absolute right-0 top-0">
					This candidate has shown interest in this position.
				</div>
			) : null}
			<h1
				className={classNames(
					'text-[#28293D] dark:text-white text-left text-xl md:text-2xl font-[700] font-inter gap-[2px] capitalize @sm/profile-introduction:max-w-full',
					{ 'max-w-[80%]': userProfileInfo.name?.length > 13 },
				)}
			>
				{userProfileInfo.name?.toLowerCase()}
				{!userProfileInfo?.flags?.isTrusted ? (
					<span className="inline-flex ml-1">
						{isRecruiterSide ? (
							<MuiTooltip title={<MuiVerifiedTooltip />} arrow>
								<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={16} height={16} />
							</MuiTooltip>
						) : (
							<MuiTooltip
								title={<VerifiedTooltip />}
								placement="bottom"
								arrow={false}
								PopperProps={{
									sx: {
										'& .MuiTooltip-tooltip': { backgroundColor: 'transparent !important', padding: '0px !important' },
									},
								}}
							>
								<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={16} height={16} />
							</MuiTooltip>
						)}
					</span>
				) : null}
			</h1>

			<div className="flex gap-1 flex-col">
				{userProfileInfo.userPreference?.experienceLevel ? (
					<Tooltip
						content={<p className="text-white">Total Experience</p>}
						trigger="hover"
						placement="bottom"
						arrow={false}
						className="mb-0 !rounded-[5px] dark:bg-[#1F2A37] dark:text-[#8b8e94] sm:right-0 sm:!max-w-[255px]"
					>
						<div className="flex gap-1 md:gap-2 items-center">
							<div>
								<HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2] w-[12px] md:w-[20px] h-[12px] md:h-[20px]" />
							</div>
							<div className="text-[#28293D] dark:text-[#E4E4EB] text-xs md:text-[14px]">
								{userProfileInfo?.userPreference?.experienceLevel === UserExperienceLevelType.FINAL_YEAR_STUDENT
									? 'Final Year Student'
									: userProfileInfo?.userPreference?.experienceLevel === UserExperienceLevelType.FRESHER
									  ? 'Fresher'
									  : userProfileInfo?.userPreference?.experienceLevel === UserExperienceLevelType.OneToTwoYears
									    ? '1-2 years experience'
									    : userProfileInfo?.userPreference?.experienceLevel === UserExperienceLevelType.TwoPlusYears
									      ? '2+ years experience'
									      : 'Fresher'}
							</div>
						</div>
					</Tooltip>
				) : null}

				{specialisation || userProfileInfo.profile?.gradYear?.toString().length > 0 ? (
					<Tooltip
						content={
							<p className="text-white">{specialisation ? 'Specialisation & Graduation Year' : 'Graduation Year'}</p>
						}
						trigger="hover"
						placement="bottom"
						arrow={false}
						className="mb-0 !rounded-[5px] dark:bg-[#1F2A37] dark:text-[#8b8e94] sm:right-0 sm:!max-w-[255px]"
					>
						<div className="flex gap-1 md:gap-2 items-center">
							<div>
								<HiAcademicCap className="text-[#00B7C4] dark:text-[#A9EFF2] w-[12px] md:w-[20px] h-[12px] md:h-[20px]" />
							</div>

							<div className="text-[#28293D] dark:text-[#E4E4EB] text-xs md:text-[14px]">
								{specialisation
									? `${specialisation} (${
											userProfileInfo.profile?.gradYear === -1
												? '2015'
												: userProfileInfo.profile?.gradYear === 1
												  ? '2027'
												  : userProfileInfo.profile?.gradYear <= currYear
												    ? userProfileInfo.profile?.gradYear
												    : userProfileInfo.profile?.gradYear || 'future'
									  })`
									: userProfileInfo.profile?.gradYear === -1
									  ? 'Graduated before 2015'
									  : userProfileInfo.profile?.gradYear === 1
									    ? 'Graduating after 2027'
									    : userProfileInfo.profile?.gradYear <= currYear
									      ? `Graduated in ${userProfileInfo.profile?.gradYear}`
									      : `Graduating in ${userProfileInfo.profile?.gradYear || 'future'}`}
							</div>
						</div>
					</Tooltip>
				) : null}

				{userProfileInfo?.profile?.userLocation ? (
					<Tooltip
						content={<p className="text-white">Current Location: {userProfileInfo?.profile?.userLocation}</p>}
						trigger="hover"
						placement="bottom"
						arrow={false}
						className="mb-0 !rounded-[5px] dark:bg-[#1F2A37] dark:text-[#8b8e94] sm:right-0 sm:!max-w-[255px]"
					>
						<div className="md:flex gap-1 md:gap-2 items-center hidden">
							<div>
								<HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[12px] md:w-[20px] h-[12px] md:h-[20px]" />
							</div>

							<div className="text-[#28293D] dark:text-[#E4E4EB] text-[14px] max-w-[400px] truncate whitespace-pre">
								{userProfileInfo?.profile?.userLocation}
							</div>
						</div>
					</Tooltip>
				) : userProfileInfo?.profile?.preferredJobLocations?.length ? (
					<Tooltip
						content={
							<p className="text-white">
								Preferred Job Locations:{' '}
								{userProfileInfo?.subRole === 'MasaiAlum' ||
								userProfileInfo?.subRole === 'MasaiStudent'
									? 'PAN India'
									: userProfileInfo?.profile?.preferredJobLocations?.join(' | ')}
							</p>
						}
						trigger="hover"
						placement="bottom"
						arrow={false}
						className="mb-0 !rounded-[5px] dark:bg-[#1F2A37] dark:text-[#8b8e94] sm:right-0 sm:!max-w-[255px]"
					>
						<div className="md:flex gap-1 md:gap-2 items-center hidden">
							<div>
								<HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[12px] md:w-[20px] h-[12px] md:h-[20px]" />
							</div>

							<div className="text-[#28293D] dark:text-[#E4E4EB] text-[14px] max-w-[400px] truncate whitespace-pre">
								{userProfileInfo?.subRole === 'MasaiAlum' ||
								userProfileInfo?.subRole === 'MasaiStudent'
									? 'PAN India'
									: userProfileInfo?.profile?.preferredJobLocations?.join(' | ')}
							</div>
						</div>
					</Tooltip>
				) : null}
			</div>
		</>
	);
};

export default ProfileExpEduLoc;
